import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField,
  Snackbar, Alert, Typography,
} from '@mui/material';
import {
  buscarRequisicaoProcessoService,
  gerarRequisicaoProcessoService,
  atenderRequisicaoProcessoService,
} from '../../../../services/core/processos/processosRequisicoesService';

const MENSAGEM_ERRO = 'Não foi possível salvar a requisição.';
const CAMPOS_FORMULARIO = ['idRequisicao', 'texto'];

function makeFormFields(requisicao) {
  return {
    idRequisicao: requisicao?.idRequisicao || '',
    texto: requisicao?.texto || '',
  };
}

/**
 * Espelha `AcaoRequisicao.jsx`/`acaoRequisicaoController.js` (tela real de
 * Inspecionar Processo, AW2-912) com fidelidade total.
 *
 * A permissão de atender ("risco alto" no critério de aceite real) é
 * validada pelo backend — o real hardcoda `temPermissaoAtender: true` no
 * payload ao atender (ver `AcaoRequisicao.jsx`, `onSubmitHandler`), portado
 * aqui do mesmo jeito; o gate de fato fica no ícone/botão que abre este
 * painel (habilitado/desabilitado conforme `podeAtenderRequisicao`,
 * calculado pelo container — Etapa 4), não dentro deste componente.
 */
function AcaoRequisicaoProcesso({ idProcesso, requisicoesOptions, onSucesso }) {
  const [hasRequisicao, setHasRequisicao] = useState(null);
  const [formFields, setFormFields] = useState(() => makeFormFields());
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [errosPorCampo, setErrosPorCampo] = useState({});

  useEffect(() => {
    setCarregando(true);
    setErro('');
    setErrosPorCampo({});
    let ignore = false;

    buscarRequisicaoProcessoService(idProcesso)
      .then((response) => {
        if (ignore) {
          return;
        }
        setFormFields(makeFormFields(response?.data));
        if (response?.data?.idRequisicao) {
          setHasRequisicao(true);
        }
      })
      .catch(() => {
        if (ignore) {
          return;
        }
        setFormFields(makeFormFields());
        setHasRequisicao(false);
      })
      .finally(() => {
        if (!ignore) {
          setCarregando(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [idProcesso]);

  const onChangeIdRequisicao = useCallback((event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, idRequisicao: value }));
    setErrosPorCampo(erros => ({ ...erros, idRequisicao: undefined }));
  }, []);

  const onChangeTexto = useCallback((event) => {
    const { value } = event.target;
    setFormFields(fields => ({ ...fields, texto: value }));
    setErrosPorCampo(erros => ({ ...erros, texto: undefined }));
  }, []);

  const onSubmit = useCallback(async (event) => {
    event.preventDefault();

    try {
      setSalvando(true);
      setErrosPorCampo({});
      const payload = {
        idProcesso,
        idRequisicao: formFields.idRequisicao,
        texto: formFields.texto,
        temPermissaoAtender: hasRequisicao ? true : undefined,
      };

      if (hasRequisicao) {
        await atenderRequisicaoProcessoService(payload);
      } else {
        await gerarRequisicaoProcessoService(payload);
      }

      onSucesso();
    } catch (exception) {
      const validations = exception.response?.data?.validations;
      const errosMapeados = (validations || []).reduce((acc, validation) => (
        CAMPOS_FORMULARIO.includes(validation.field)
          ? { ...acc, [validation.field]: validation.message }
          : acc
      ), {});

      if (Object.keys(errosMapeados).length > 0) {
        setErrosPorCampo(errosMapeados);
      } else {
        setErro(MENSAGEM_ERRO);
      }
    } finally {
      setSalvando(false);
    }
  }, [idProcesso, formFields, hasRequisicao, onSucesso]);

  const desabilitarSubmit = useMemo(
    () => carregando || salvando || !formFields.idRequisicao || formFields.texto.trim().length === 0,
    [carregando, salvando, formFields],
  );

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {hasRequisicao ? 'Atendimento de Requisição' : 'Geração de Requisição'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 2 }}>
        O Comentário será inserido no Histórico do Processo
      </Typography>

      <FormControl
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        disabled={hasRequisicao}
        error={Boolean(errosPorCampo.idRequisicao)}
      >
        <InputLabel id="nome-requisicao-label">Nome da Requisição*</InputLabel>
        <Select
          labelId="nome-requisicao-label"
          label="Nome da Requisição*"
          value={formFields.idRequisicao}
          onChange={onChangeIdRequisicao}
        >
          {requisicoesOptions.map(opcao => (
            <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
          ))}
        </Select>
        {errosPorCampo.idRequisicao && (
          <FormHelperText>{errosPorCampo.idRequisicao}</FormHelperText>
        )}
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={8}
        label={hasRequisicao ? 'Resposta da Solicitação' : 'Texto'}
        placeholder={hasRequisicao ? 'Inserir Resposta' : 'Inserir Texto'}
        value={formFields.texto}
        onChange={onChangeTexto}
        error={Boolean(errosPorCampo.texto)}
        helperText={errosPorCampo.texto}
        sx={{ mb: 2 }}
      />

      <Button type="submit" variant="contained" color="success" disabled={desabilitarSubmit}>
        {hasRequisicao ? 'Atender' : 'Concluir'}
      </Button>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

AcaoRequisicaoProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  requisicoesOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.string,
  })),
  onSucesso: PropTypes.func.isRequired,
};

AcaoRequisicaoProcesso.defaultProps = {
  idProcesso: null,
  requisicoesOptions: [],
};

export default React.memo(AcaoRequisicaoProcesso);
